import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import DataTable from '../../components/ui/DataTable';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import ImageUpload from '../../components/admin/ImageUpload';
import { adminTheme } from '../../theme-admin';
import { cache } from '../../utils/cache';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const CreateButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  background: ${adminTheme.gradients.primary};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.bold};
  cursor: pointer;
  box-shadow: ${adminTheme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }
`;

const TableWrapper = styled.div`
  background: white;
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
`;

const ModalForm = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
`;

const Input = styled.input`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const TextArea = styled.textarea`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  min-height: 100px;
  resize: vertical;
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const ActionButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: white;
  margin-right: ${adminTheme.spacing.sm};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
  }
`;

const DeleteButton = styled(ActionButton)`
  border-color: ${adminTheme.colors.danger[300]};
  background: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.danger[600]};
  }
`;

const StarRating = styled.div`
  display: flex;
  gap: 4px;
`;

const Star = styled.span`
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.filled ? '#fbbf24' : '#e5e7eb'};
  
  &:hover {
    color: #fbbf24;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
  padding: ${adminTheme.spacing.md};
  background: ${adminTheme.colors.gray[50]};
  border-radius: ${adminTheme.borderRadius.lg};
`;

function PageBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const toast = useToast();

  const getInitialForm = () => ({
    name: '',
    role: '',
    company: '',
    quote: '',
    avatarUrl: '',
    rating: 5,
    featured: false,
    order: 0
  });

  const [editForm, setEditForm] = useState(getInitialForm());

  const load = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = cache.get('testimonials-list-admin');
      if (cached) {
        setItems(cached);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/testimonials?includeAll=true`);
      const data = await res.json();
      const testimonials = data.data?.testimonials || [];
      setItems(testimonials);
      cache.set('testimonials-list-admin', testimonials);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
    // toast is stable from react-toastify
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsCreating(true);
    setEditing(null);
    setEditForm(getInitialForm());
  };

  const openEdit = (t) => {
    setIsCreating(false);
    setEditing(t);
    setEditForm({
      name: t.name || '',
      role: t.role || '',
      company: t.company || '',
      quote: t.quote || '',
      avatarUrl: t.avatarUrl || '',
      rating: t.rating || 5,
      featured: t.featured || false,
      order: t.order || 0
    });
  };

  const closeModal = () => {
    setIsCreating(false);
    setEditing(null);
    setEditForm(getInitialForm());
  };

  const saveTestimonial = async () => {
    try {
      const url = isCreating
        ? `${API_BASE_URL}/testimonials`
        : `${API_BASE_URL}/testimonials/${editing._id}`;
      
      const method = isCreating ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(editForm)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Operation failed');
      }

      toast.success(isCreating ? 'Testimonial created! 🎉' : 'Testimonial updated! ✨');
      closeModal();
      cache.invalidate('testimonials-list');
      load(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save testimonial');
    }
  };

  const onDelete = async (id) => {
    setConfirmId(id);
  };

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/testimonials/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('delete failed');
      toast.success('Testimonial deleted');
      cache.invalidate('testimonials-list');
      load(true);
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const renderStars = (currentRating, onChange) => {
    return (
      <StarRating>
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            filled={star <= currentRating}
            onClick={() => onChange && onChange(star)}
          >
            ★
          </Star>
        ))}
      </StarRating>
    );
  };

  return (
    <div>
      <PageHeader>
        <Title>
          <span>⭐</span>
          Testimonials Management
        </Title>
        <CreateButton onClick={openCreate}>
          <span>➕</span>
          Create New Testimonial
        </CreateButton>
      </PageHeader>

      <TableWrapper>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner label="Loading testimonials..." />
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'name', header: 'Name', width: '1fr' },
              { key: 'role', header: 'Role', width: '1fr' },
              { key: 'company', header: 'Company', width: '1fr' },
              { 
                key: 'rating', 
                header: 'Rating', 
                width: '0.8fr',
                render: (t) => (
                  <div style={{ display: 'flex', gap: '2px', fontSize: '14px' }}>
                    {t.rating ? '⭐'.repeat(t.rating) : 'N/A'}
                  </div>
                )
              },
              { key: 'quote', header: 'Quote', width: '2fr' },
            ]}
            rows={items}
            actions={(t) => (
              <>
                <ActionButton onClick={() => openEdit(t)}>✏️ Edit</ActionButton>
                <DeleteButton onClick={() => onDelete(t._id)}>🗑️ Delete</DeleteButton>
              </>
            )}
          />
        )}
      </TableWrapper>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={confirmDelete}
        title="Delete testimonial?"
        description="This action cannot be undone."
      />

      <Modal
        open={isCreating || !!editing}
        title={isCreating ? '✨ Create New Testimonial' : '✏️ Edit Testimonial'}
        onCancel={closeModal}
        onSave={saveTestimonial}
        saveLabel={isCreating ? 'Create Testimonial' : 'Save Changes'}
      >
        <ModalForm>
          <FormGroup>
            <Label>Name *</Label>
            <Input
              placeholder="John Doe"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Role *</Label>
            <Input
              placeholder="Senior Developer"
              value={editForm.role}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Company *</Label>
            <Input
              placeholder="Tech Company Inc."
              value={editForm.company}
              onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Quote * (max 500 characters)</Label>
            <TextArea
              placeholder="Share your experience..."
              value={editForm.quote}
              onChange={(e) => setEditForm({ ...editForm, quote: e.target.value })}
              maxLength={500}
              required
            />
            <small style={{ color: '#6b7280' }}>
              {editForm.quote.length}/500 characters
            </small>
          </FormGroup>

          <FormGroup>
            <ImageUpload
              label="Avatar"
              value={editForm.avatarUrl}
              onChange={(url) => setEditForm({ ...editForm, avatarUrl: url })}
              folder="testimonials"
            />
          </FormGroup>

          <FormGroup>
            <Label>Rating</Label>
            {renderStars(editForm.rating, (rating) => setEditForm({ ...editForm, rating }))}
          </FormGroup>

          <FormGroup>
            <Label>Order (for sorting)</Label>
            <Input
              type="number"
              placeholder="0"
              value={editForm.order}
              onChange={(e) => setEditForm({ ...editForm, order: parseInt(e.target.value) || 0 })}
            />
          </FormGroup>

          <CheckboxGroup>
            <input
              type="checkbox"
              checked={editForm.featured}
              onChange={(e) => setEditForm({ ...editForm, featured: e.target.checked })}
              id="featured-testimonial"
            />
            <Label htmlFor="featured-testimonial">⭐ Featured Testimonial</Label>
          </CheckboxGroup>
        </ModalForm>
      </Modal>
    </div>
  );
}

export default function AdminTestimonials() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
